<template>
  <div class="container">
    <div v-if="loading" class="loading-container">
      <i class="fa fa-spinner fa-spin"></i> Loading details...
    </div>

    <div v-else>
      <div class="section-card">
        <div class="section-header-row">
          <h4 class="section-title mb-0">Route Information</h4>
        </div>
        
        <div class="detail-grid">
          <div class="detail-item">
            <label>Route Name</label>
            <div class="value">{{ route.route_name }}</div>
          </div>
          <div class="detail-item">
            <label>Distance</label>
            <div class="value">{{ route.distance }} Km</div>
          </div>
          <div class="detail-item">
            <label>Start Point</label>
            <div class="value">{{ route.start_point }}</div>
          </div>
          <div class="detail-item">
            <label>End Point</label>
            <div class="value">{{ route.end_point }}</div>
          </div>
          <div class="detail-item">
            <label>Distributor Name</label>
            <div class="value">{{ route.distributor_name }}</div>
          </div>
          <div class="detail-item">
            <label>Status</label>
            <div>
              <span :class="route.is_active == 1 ? 'status-badge active' : 'status-badge inactive'">
                {{ route.is_active == 1 ? 'Active' : 'Inactive' }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="section-card">
        <div class="section-header-row">
          <h4 class="section-title mb-0">Dealers of the Route</h4>
          
          <div class="search-container">
            <input 
              type="text" 
              v-model="searchQuery" 
              placeholder="Search by dealer name..." 
              class="form-control search-input"
            />
            <i class="fa fa-search search-icon"></i>
          </div>
        </div>

        <div class="table-responsive">
          <table class="table table-bordered">
            <thead>
              <tr>
                <th>Dealer Name</th>
                <th>Address</th>
                <th>City</th>
                <th class="text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="dealer in filteredDealers" :key="dealer.id">
                <td>{{ dealer.business_name }}</td>
                <td>{{ dealer.address }}</td>
                <td>{{ dealer.city }}</td> 
                <td class="text-center">
                  <span :class="dealer.is_active == 1 ? 'status-badge active' : 'status-badge inactive'">
                    {{ dealer.is_active == 1 ? 'Active' : 'Inactive' }}
                  </span>
                </td>
              </tr>
              <tr v-if="filteredDealers.length === 0">
                <td colspan="4" class="text-center text-muted">
                  {{ searchQuery ? 'No dealers found matching your search.' : 'No dealers assigned to this route.' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { viewRouteDetails } from "@/services/dataService";

export default {
  name: "ViewRoute",
  props: {
    routeId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: true,
      token: localStorage.getItem("token_set") || "",
      searchQuery: "", // Added for search functionality
      route: {},
      dealers: []
    };
  },
  computed: {
    // Added computed property for filtering
    filteredDealers() {
      if (!this.searchQuery) {
        return this.dealers;
      }
      const query = this.searchQuery.toLowerCase();
      return this.dealers.filter(dealer => 
        (dealer.business_name || '').toLowerCase().includes(query)
      );
    }
  },
  mounted() {
    this.fetchRouteDetails();
  },
  methods: {
    async fetchRouteDetails() {
      try {
        this.loading = true;
        const response = await viewRouteDetails(this.token, this.routeId);

        if (response.data && response.data.data && response.data.data.routeDetails && response.data.data.routeDealers) {
          this.route = response.data.data.routeDetails;
          this.dealers = response.data.data.routeDealers;
        } else {
          this.route = {};
          this.dealers = [];
        }
      } catch (error) {
        console.error("Error fetching route details:", error);
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
html, body {
  height: 100%;
  margin: 0;
  padding: 0;
  background-color: #f0f0f0;
}

.container {
  background-color: #ffffff;
  padding: 20px 15px;
  margin: 10px auto;
  width: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  box-sizing: border-box;
  max-width: 100%;
}

.section-card {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 20px;
  margin-bottom: 20px;
  background-color: #fff;
}

.section-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 5px;
  flex-wrap: wrap;
  gap: 10px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  text-transform: uppercase;
  margin-bottom: 0; /* Remove bottom margin as flex handles spacing */
}

.search-container {
  position: relative;
  width: 300px;
  max-width: 100%;
}

.search-input {
  width: 100%;
  padding: 8px 30px 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
}

.search-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  font-size: 14px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
}

.detail-item {
  display: flex;
  flex-direction: column;
}

.detail-item label {
  font-size: 12px;
  color: #6c757d;
  text-transform: uppercase;
  margin-bottom: 4px;
  font-weight: 600;
}

.detail-item .value {
  font-size: 15px;
  font-weight: 500;
  color: #212529;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  min-width: 80px; 
  text-align: center;
}

.status-badge.active { 
  background-color: #d4edda; 
  color: #155724; 
}

.status-badge.inactive { 
  background-color: #f8d7da; 
  color: #721c24; 
}

.table-responsive { 
  overflow-x: auto; 
  margin-top: 15px; 
}

.table {
  width: 100%;
  border-collapse: collapse;
}

.table th, .table td {
  padding: 12px 15px;
  border: 1px solid #dee2e6;
  text-align: left;
}

.table th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #495057;
}

.text-center { 
  text-align: center; 
}

.text-muted { 
  color: #6c757d; 
  font-style: italic; 
  padding: 20px; 
}

.mb-0 { 
  margin-bottom: 0 !important; 
}

.loading-container {
  text-align: center;
  padding: 50px;
  color: #007bff;
  font-size: 18px;
}
</style>